import { loadStripe } from '@stripe/stripe-js';
import type {
  Appearance,
  Stripe,
  StripeElements,
  StripeError,
  StripePaymentElement,
} from '@stripe/stripe-js';

interface StripePaymentHook {
  el: HTMLElement;
  pushEvent: (
    event: string,
    payload: object,
    onReply?: (reply: unknown, ref: number) => void
  ) => void;
  submitHandler?: (e: Event) => void;
  themeObserver?: MutationObserver;
  stripeInstance?: Stripe;
  stripeElements?: StripeElements;
  paymentElement?: StripePaymentElement;
  paymentComplete?: boolean;
  processing?: boolean;
  mountPaymentElement?: (secret: string) => void;
  handleError: (
    msg: string,
    btn?: HTMLButtonElement | null,
    text?: string,
    container?: HTMLElement | null,
    rawError?: StripeError | Error
  ) => void;
}

/**
 * Resolve a CSS custom property to an rgb() string that Stripe can consume.
 * Stripe's Appearance API doesn't parse oklch / relative-colour syntax natively,
 * so we let the browser resolve the variable by setting it on a hidden-but-laid-out
 * element and reading the computed value.
 *
 * IMPORTANT: use `visibility:hidden` + `position:absolute`, NOT `display:none`.
 * display:none elements are excluded from layout, so browsers skip computing
 * CSS custom property chains (including relative-colour syntax). visibility:hidden
 * keeps the element in the layout tree, guaranteeing full resolution.
 */
function resolveColor(property: string, fallback: string): string {
  try {
    const tmp = document.createElement('div');
    tmp.style.cssText = `color:var(${property});position:absolute;visibility:hidden`;
    document.body.appendChild(tmp);
    const value = getComputedStyle(tmp).color;
    document.body.removeChild(tmp);
    return value || fallback;
  } catch {
    return fallback;
  }
}

function buildAppearance(): Appearance {
  // Resolve primary dynamically (brand colour can change); all dark-mode surface
  // colours are hardcoded to match app.css dark tokens exactly, bypassing the
  // CSS-variable resolution chain that Stripe's iframe ignores anyway.
  const colorPrimary = resolveColor('--color-primary', '#e8722a');

  const isDark =
    (globalThis as { getTheme?: () => string }).getTheme?.() === 'dark' ||
    document.documentElement.classList.contains('dark');

  const colorDanger = resolveColor('--color-negative', '#e04820');

  // Light mode: use Stripe's own clean defaults, only inject brand colour.
  if (!isDark) {
    return {
      theme: 'stripe',
      variables: {
        colorPrimary,
        colorDanger,
        fontFamily:
          'Rajdhani, ui-sans-serif, system-ui, -apple-system, sans-serif',
        fontSizeBase: '14px',
        fontWeightNormal: '500',
        fontWeightMedium: '600',
        fontWeightBold: '700',
        borderRadius: '6px',
        spacingUnit: '4px',
        spacingGridRow: '16px',
        focusBoxShadow: 'none',
        focusOutline: `2px solid ${colorPrimary}`,
      },
      rules: {
        '.Input:focus': {
          border: `1px solid ${colorPrimary}`,
          boxShadow: 'none',
          outline: `2px solid ${colorPrimary}`,
          outlineOffset: '0px',
        },
        '.Tab': { color: colorPrimary },
        '.Tab:hover': { color: colorPrimary },
        '.Tab--selected': { color: colorPrimary },
        '.TabIcon': { fill: colorPrimary },
        '.TabIcon--selected': { fill: colorPrimary },
        '.TabIcon:hover': { fill: colorPrimary },
        '.TabLabel': { color: colorPrimary },
        '.TabLabel--selected': { color: colorPrimary },
        '.Error': { color: colorDanger, fontSize: '12px' },
      },
    };
  }

  // Dark mode: night theme keeps Stripe's structural CSS (icons, card form) intact.
  // Its outer container is always #30313d — we match our wrapper to that colour.
  const colorBackground = '#30313d';
  const colorInputBg = '#3a3b46';
  const colorText = '#f8f8f8';
  const colorPlaceholder = 'rgba(248,248,248,0.45)';
  const colorBorder = 'rgba(248,248,248,0.14)';
  const colorBorderFocus = 'rgba(248,248,248,0.28)';

  return {
    theme: 'night',
    variables: {
      colorPrimary,
      colorBackground,
      colorText,
      colorDanger,
      colorTextPlaceholder: colorPlaceholder,
      colorTextSecondary: colorPlaceholder,
      colorIconCardCvc: colorText,
      colorIconCardCvcError: colorDanger,
      colorIconTab: colorPlaceholder,
      colorIconTabSelected: colorText,
      colorIconTabHover: colorText,
      fontFamily:
        'Rajdhani, ui-sans-serif, system-ui, -apple-system, sans-serif',
      fontSizeBase: '14px',
      fontWeightNormal: '500',
      fontWeightMedium: '600',
      fontWeightBold: '700',
      borderRadius: '6px',
      spacingUnit: '4px',
      spacingGridRow: '16px',
      focusBoxShadow: 'none',
      focusOutline: `2px solid ${colorPrimary}`,
    },
    rules: {
      '.Input': {
        backgroundColor: colorInputBg,
        border: `1px solid ${colorBorder}`,
        color: colorText,
        boxShadow: 'none',
        padding: '10px 12px',
      },
      '.Input:hover': {
        border: `1px solid ${colorBorderFocus}`,
      },
      '.Input:focus': {
        border: `1px solid ${colorPrimary}`,
        boxShadow: 'none',
        outline: `2px solid ${colorPrimary}`,
        outlineOffset: '0px',
      },
      '.Input--invalid': {
        border: `1px solid ${colorDanger}`,
      },
      '.Label': {
        color: colorPlaceholder,
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      },
      '.Tab': {
        backgroundColor: colorBackground,
        border: `1px solid ${colorBorder}`,
        boxShadow: 'none',
        color: colorPlaceholder,
      },
      '.Tab:hover': {
        backgroundColor: colorInputBg,
        border: `1px solid ${colorBorderFocus}`,
        color: colorText,
      },
      '.Tab--selected': {
        backgroundColor: colorBackground,
        border: `1px solid ${colorPrimary}`,
        color: colorText,
        boxShadow: 'none',
      },
      '.Tab--selected:focus': {
        boxShadow: 'none',
      },
      '.TabIcon--selected': {
        fill: colorPrimary,
      },
      '.TabLabel--selected': {
        color: colorText,
      },
      '.Block': {
        backgroundColor: colorInputBg,
        border: `1px solid ${colorBorder}`,
        boxShadow: 'none',
      },
      '.CheckboxInput': {
        backgroundColor: colorInputBg,
        border: `1px solid ${colorBorder}`,
      },
      '.CheckboxInput--checked': {
        backgroundColor: colorPrimary,
        border: `1px solid ${colorPrimary}`,
      },
      '.PickerItem': {
        backgroundColor: colorBackground,
        border: `1px solid ${colorBorder}`,
        color: colorText,
        boxShadow: 'none',
      },
      '.PickerItem:hover': {
        backgroundColor: colorInputBg,
        border: `1px solid ${colorBorderFocus}`,
      },
      '.PickerItem--selected': {
        backgroundColor: colorBackground,
        border: `1px solid ${colorPrimary}`,
      },
      '.PickerItem--selected:hover': {
        backgroundColor: colorInputBg,
      },
      '.Error': {
        color: colorDanger,
        fontSize: '12px',
      },
    },
  };
}

function isPromoDeferredCheckout(el: HTMLElement): boolean {
  const value = el.dataset['promoDeferred'];
  if (value === 'true') return true;
  // HEEx may render a present boolean attribute as an empty string.
  return el.hasAttribute('data-promo-deferred') && value !== 'false';
}

function setSubmitEnabled(
  submitBtn: HTMLButtonElement | null,
  enabled: boolean,
  processing: boolean
) {
  if (!submitBtn) return;
  submitBtn.disabled = processing || !enabled;
  submitBtn.classList.toggle('opacity-50', submitBtn.disabled);
  submitBtn.classList.toggle('cursor-not-allowed', submitBtn.disabled);
}

function bindPaymentElement(
  hook: StripePaymentHook,
  paymentElement: StripePaymentElement,
  submitBtn: HTMLButtonElement | null
) {
  hook.paymentElement = paymentElement;
  hook.paymentComplete = false;
  setSubmitEnabled(submitBtn, false, Boolean(hook.processing));

  paymentElement.on('ready', () => {
    if (!hook.processing) {
      setSubmitEnabled(submitBtn, Boolean(hook.paymentComplete), false);
    }
  });

  paymentElement.on('change', (event) => {
    hook.paymentComplete = event.complete;
    if (!hook.processing) {
      setSubmitEnabled(submitBtn, event.complete, false);
    }
  });
}

const StripePayment = {
  async mounted(this: StripePaymentHook) {
    const publicKey = this.el.dataset['publicKey'];
    const clientSecret = this.el.dataset['clientSecret'] || '';
    const promoDeferred = isPromoDeferredCheckout(this.el);
    const submitBtn = document.getElementById(
      'submit-payment'
    ) as HTMLButtonElement | null;
    const messageContainer = document.getElementById('payment-message');

    this.handleError = (msg, btn, text, container, rawError) => {
      if (container) {
        container.textContent = msg;
        container.classList.remove('hidden');
      }

      const errorCode =
        rawError && 'code' in rawError ? rawError.code : 'initialization_error';
      const errorType =
        rawError && 'type' in rawError ? rawError.type : 'setup_failure';

      this.pushEvent('stripe_hook_error', {
        message: msg,
        code: errorCode,
        type: errorType,
      });

      if (btn && text) {
        btn.innerText = text;
        setSubmitEnabled(btn, Boolean(this.paymentComplete), false);
      }
    };

    if (!publicKey) {
      this.handleError(
        'Payment system is not configured (missing Stripe publishable key). Please contact support.',
        submitBtn,
        'Unavailable',
        messageContainer
      );
      return;
    }

    if (!clientSecret && !promoDeferred) {
      this.handleError(
        'Payment form is not ready yet. Wait a moment and try again, or refresh the page.',
        submitBtn,
        'Pay & register',
        messageContainer
      );
      return;
    }

    try {
      const stripe: Stripe | null = await loadStripe(publicKey);
      if (!stripe) throw new Error('Stripe.js failed to load.');
      this.stripeInstance = stripe;

      const fonts = [
        {
          cssSrc:
            'https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&display=swap',
        },
      ];

      const paymentMountEl = (): HTMLElement | null =>
        this.el.querySelector('[id^="payment-element-"]');

      const mountElements = (secret: string) => {
        const mountTarget = paymentMountEl();
        if (!mountTarget) {
          throw new Error('Missing payment element mount point.');
        }

        this.stripeElements = stripe.elements({
          clientSecret: secret,
          appearance: buildAppearance(),
          fonts,
        });
        const paymentElement = this.stripeElements.create('payment');
        mountTarget.innerHTML = '';
        mountTarget.classList.remove('hidden');
        this.el
          .querySelector('#promo-payment-pending')
          ?.classList.add('hidden');
        paymentElement.mount(mountTarget);
        bindPaymentElement(this, paymentElement, submitBtn);
      };

      this.mountPaymentElement = (secret: string) => {
        mountElements(secret);
      };

      if (clientSecret) {
        mountElements(clientSecret);
      } else if (promoDeferred) {
        setSubmitEnabled(submitBtn, true, false);
      }

      // Stripe's theme cannot be changed via elements.update() after init —
      // only variables/rules are updatable. To genuinely switch between the
      // night (dark) and stripe (light) themes we must recreate the elements
      // group from scratch each time the html[class] attribute changes.
      let lastIsDark = document.documentElement.classList.contains('dark');
      this.themeObserver = new MutationObserver(() => {
        const nowDark = document.documentElement.classList.contains('dark');
        if (nowDark === lastIsDark) return;
        lastIsDark = nowDark;
        const secret = this.el.dataset['clientSecret'] || clientSecret;
        if (!secret) return;
        const paymentMount = this.el.querySelector('[id^="payment-element-"]');
        if (!paymentMount) return;
        paymentMount.innerHTML = '';
        mountElements(secret);
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      const remountElements = (nextClientSecret: string, nextMode: string) => {
        this.el.dataset['clientSecret'] = nextClientSecret;
        this.el.dataset['checkoutMode'] = nextMode;
        this.el.dataset['promoDeferred'] = 'false';
        mountElements(nextClientSecret);
      };

      const showPaymentMessage = (msg: string) => {
        if (!messageContainer) return;
        messageContainer.textContent = msg;
        messageContainer.classList.remove('hidden');
      };

      this.submitHandler = (e: Event) => {
        const handle = async () => {
          e.preventDefault();
          if (!submitBtn || !this.stripeInstance) return;
          if (this.processing) return;

          const promoDeferredNow = isPromoDeferredCheckout(this.el);

          if (!this.stripeElements && !promoDeferredNow) {
            showPaymentMessage(
              'Payment form is not ready. Please refresh the page and try again.'
            );
            return;
          }

          if (this.stripeElements && !this.paymentComplete) {
            showPaymentMessage(
              'Please choose a payment method and complete the card details before continuing.'
            );
            setSubmitEnabled(submitBtn, false, false);
            return;
          }

          if (messageContainer) {
            messageContainer.classList.add('hidden');
            messageContainer.textContent = '';
          }

          this.processing = true;
          const originalText = submitBtn.innerText;
          submitBtn.innerText = 'Processing...';
          setSubmitEnabled(submitBtn, true, true);

          const secretBeforePrepare =
            this.el.dataset['clientSecret'] || clientSecret || '';
          let checkoutMode = this.el.dataset['checkoutMode'] || 'payment';

          try {
            type PrepareSignupPaymentReply = {
              ok?: boolean;
              error?: string;
              client_secret?: string;
              checkout_mode?: string;
            };

            const prep = await new Promise<PrepareSignupPaymentReply>(
              (resolve) => {
                this.pushEvent(
                  'prepare_signup_payment',
                  {},
                  (reply: unknown) => {
                    resolve((reply ?? {}) as PrepareSignupPaymentReply);
                  }
                );
              }
            );

            if (!prep.ok) {
              this.handleError(
                prep.error ??
                  'Could not apply your promotion code to payment. Please try again.',
                submitBtn,
                originalText,
                messageContainer
              );
              return;
            }

            const nextSecret = prep.client_secret || secretBeforePrepare;
            checkoutMode = prep.checkout_mode || checkoutMode;

            if (nextSecret && nextSecret !== secretBeforePrepare) {
              remountElements(nextSecret, checkoutMode);
              setSubmitEnabled(submitBtn, false, false);
              showPaymentMessage(
                'Enter your payment details below, then click Pay & register again.'
              );
              submitBtn.innerText = originalText;
              this.processing = false;
              return;
            }

            if (!this.stripeElements) {
              showPaymentMessage(
                'Could not load the payment form. Please refresh and try again.'
              );
              return;
            }

            const { error: submitError } = await this.stripeElements.submit();

            if (submitError) {
              this.handleError(
                submitError.message ??
                  'Please complete your payment details before continuing.',
                submitBtn,
                originalText,
                messageContainer,
                submitError
              );
              return;
            }

            const returnUrl =
              this.el.dataset['returnUrl'] ||
              `${window.location.origin}/users/log_in?_action=registered`;

            const confirmParams = {
              elements: this.stripeElements,
              confirmParams: { return_url: returnUrl },
              redirect: 'if_required' as const,
            };

            if (checkoutMode === 'setup') {
              const { error, setupIntent } =
                await this.stripeInstance.confirmSetup(confirmParams);

              if (error) {
                this.handleError(
                  error.message ?? 'Payment failed',
                  submitBtn,
                  originalText,
                  messageContainer,
                  error
                );
                return;
              }

              if (setupIntent.status === 'succeeded') {
                window.location.assign(returnUrl);
                return;
              }
            } else {
              const { error, paymentIntent } =
                await this.stripeInstance.confirmPayment(confirmParams);

              if (error) {
                this.handleError(
                  error.message ?? 'Payment failed',
                  submitBtn,
                  originalText,
                  messageContainer,
                  error
                );
                return;
              }

              if (paymentIntent.status === 'succeeded') {
                window.location.assign(returnUrl);
                return;
              }
            }
          } catch (err: unknown) {
            const error = err instanceof Error ? err : new Error(String(err));
            this.handleError(
              'Unexpected execution error. Please try again.',
              submitBtn,
              originalText,
              messageContainer,
              error
            );
          } finally {
            this.processing = false;
            if (submitBtn.innerText === 'Processing...') {
              submitBtn.innerText = originalText;
            }
            setSubmitEnabled(submitBtn, Boolean(this.paymentComplete), false);
          }
        };

        void handle();
      };

      if (submitBtn) {
        submitBtn.addEventListener('click', this.submitHandler);
      }
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.handleError(
        'Unable to initialise payment provider. Please contact support.',
        submitBtn,
        'Unavailable',
        messageContainer,
        error
      );
    }
  },

  updated(this: StripePaymentHook) {
    const submitBtn = document.getElementById(
      'submit-payment'
    ) as HTMLButtonElement | null;
    const messageContainer = document.getElementById('payment-message');
    const clientSecret = this.el.dataset['clientSecret'] || '';
    const promoDeferred = isPromoDeferredCheckout(this.el);
    const mountTarget = this.el.querySelector('[id^="payment-element-"]');
    const mountId = mountTarget?.id || '';
    const mountedSecret = this.el.dataset['mountedClientSecret'] || '';
    const mountedElementId = this.el.dataset['mountedElementId'] || '';

    if (promoDeferred && !clientSecret) {
      if (messageContainer) {
        messageContainer.classList.add('hidden');
        messageContainer.textContent = '';
      }

      setSubmitEnabled(submitBtn, true, false);
      return;
    }

    if (clientSecret && mountedElementId && !mountTarget) {
      return;
    }

    const needsRemount =
      Boolean(clientSecret && this.mountPaymentElement && mountTarget) &&
      (clientSecret !== mountedSecret || mountId !== mountedElementId);

    if (needsRemount) {
      const mountPaymentElement = this.mountPaymentElement;
      if (!mountPaymentElement) return;

      document.getElementById('promo-payment-pending')?.classList.add('hidden');

      if (messageContainer) {
        messageContainer.classList.add('hidden');
        messageContainer.textContent = '';
      }

      this.paymentElement?.unmount();
      this.paymentElement = undefined;
      this.stripeElements = undefined;
      this.paymentComplete = false;
      this.el.dataset['mountedClientSecret'] = clientSecret;
      this.el.dataset['mountedElementId'] = mountId;
      mountPaymentElement(clientSecret);
      setSubmitEnabled(submitBtn, false, false);
    }
  },

  destroyed(this: StripePaymentHook) {
    const submitBtn = document.getElementById('submit-payment');
    if (submitBtn && this.submitHandler) {
      submitBtn.removeEventListener('click', this.submitHandler);
    }
    this.themeObserver?.disconnect();
  },
};

export default StripePayment;
